import { RefreshCw, Moon, Sun, ArrowLeft, Users, Building2, LogOut, User, ScrollText } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Logo } from './Logo';

interface HeaderProps {
  onRefresh: () => void;
  isRefreshing?: boolean;
  lastUpdated?: string;
}

// The mobile app's menu: a rounded bar with the current item as an ink pill.
const NAV_ITEM = 'flex items-center gap-2 px-3.5 py-1.5 text-sm rounded-full transition-colors';
const NAV_ACTIVE = 'bg-ink text-on-ink font-semibold';
const NAV_IDLE = 'font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';
const ICON_BUTTON =
  'p-2 text-gray-500 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors';

const ADMIN_LINKS = [
  { to: '/admin/users', label: 'Users', Icon: Users },
  { to: '/admin/sites', label: 'Sites', Icon: Building2 },
  { to: '/admin/audit', label: 'Audit', Icon: ScrollText },
];

export function Header({ onRefresh, isRefreshing }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnSubpage = location.pathname !== '/';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              {isOnSubpage && (
                <button
                  onClick={() => navigate('/')}
                  className={`${ICON_BUTTON} hover:text-brand-orange`}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div
                className="flex items-center cursor-pointer"
                onClick={() => navigate('/')}
              >
                <Logo />
              </div>
            </div>

            {/* Admin Navigation - only show for admins */}
            {isAdmin && (
              <nav className="hidden md:flex items-center gap-0.5 p-1 rounded-full bg-card border border-gray-200/80 dark:border-gray-700/80 shadow-card">
                {ADMIN_LINKS.map(({ to, label, Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`${NAV_ITEM} ${location.pathname === to ? NAV_ACTIVE : NAV_IDLE}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </Link>
                ))}
              </nav>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className={`${ICON_BUTTON} hover:text-brand-orange`}
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            {/* Refresh button - admin only */}
            {isAdmin && (
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-50 transition-colors"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            )}

            {/* User info and logout */}
            {user && (
              <div className="flex items-center gap-1.5">
                <Link
                  to="/account"
                  title="Account settings"
                  className={`hidden sm:flex items-center gap-2 text-sm px-3 py-1.5 rounded-full transition-colors ${
                    location.pathname === '/account'
                      ? NAV_ACTIVE
                      : 'text-gray-700 dark:text-gray-300 bg-card border border-gray-200/80 dark:border-gray-700/80 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <User className="w-4 h-4 opacity-60" />
                  <span>{user.email}</span>
                  {isAdmin && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold bg-brand-orange/15 text-brand-orange rounded-full">
                      Admin
                    </span>
                  )}
                </Link>
                <button
                  onClick={handleLogout}
                  className={`${ICON_BUTTON} hover:text-red-600 dark:hover:text-red-400`}
                  title="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
